import isUndefined from 'lodash/isUndefined';
import {
  ErrorExchangeMessageAuto,
  ExchangeMessage,
  ExchangeMessageAuto,
  NativeMessageExchangeError,
} from './message';

export interface MessageCreatorFunction<TType extends string, TPayload, TMeta>
  extends Function {
  /**
   * name of ExchangeMessage type
   */
  type: TType;
  (options?: {
    payload?: TPayload;
    meta?: TMeta;
    txId?: ExchangeMessageAuto['txId'];
  }): ExchangeMessageAuto<TType, TPayload, TMeta>;
}

export interface ErrorMessageCreatorFunction<
  TType extends string,
  TCustomError extends NativeMessageExchangeError,
  TMeta
> extends Function {
  /**
   * name of ErrorExchangeMessage type
   */
  type: TType;
  (options?: {
    payload?: TCustomError;
    meta?: TMeta;
    txId?: ErrorExchangeMessageAuto['txId'];
  }): ErrorExchangeMessageAuto<TType, TCustomError, TMeta>;
}

/**
 * Default values for message properties
 */
export type MessageDefaults<P, M> = Omit<
  ExchangeMessage<string, P, M>,
  'type' | 'txId'
>;

export const createMessage = <TPayload = undefined, TMeta = undefined>(
  typeName: ExchangeMessageAuto['type'],
  defaults: MessageDefaults<TPayload, TMeta> = {}
): MessageCreatorFunction<typeof typeName, TPayload, TMeta> => {
  function messageCreator({
    payload,
    meta,
    txId,
  }: {
    payload?: TPayload;
    meta?: TMeta;
    txId?: ExchangeMessageAuto['txId'];
  } = {}) {
    const message = { ...defaults } as ExchangeMessageAuto<
      typeof typeName,
      TPayload,
      TMeta
    >;
    if (!isUndefined(payload)) message.payload = payload;
    if (!isUndefined(message)) message.meta = meta;
    if (!isUndefined(txId)) message.txId = txId;

    message.type = typeName;
    return message;
  }

  messageCreator.type = typeName;

  return messageCreator;
};

export const createErrorMessage = <
  TCustomError extends NativeMessageExchangeError,
  TMeta = undefined
>(
  typeName: ErrorExchangeMessageAuto['type'],
  defaults: MessageDefaults<TCustomError, TMeta> = {}
): ErrorMessageCreatorFunction<typeof typeName, TCustomError, TMeta> => {
  function messageCreator({
    payload,
    meta,
    txId,
  }: {
    payload?: TCustomError;
    meta?: TMeta;
    txId?: ErrorExchangeMessageAuto['txId'];
  } = {}) {
    const message = { ...defaults } as ErrorExchangeMessageAuto<
      typeof typeName,
      TCustomError,
      TMeta
    >;
    if (!isUndefined(payload)) message.payload = payload;
    if (!isUndefined(message)) message.meta = meta;
    if (!isUndefined(txId)) message.txId = txId;

    message.type = typeName;
    return message;
  }

  messageCreator.type = typeName;

  return messageCreator;
};
